import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useProjects as _useProjects} from 'components/hooks/useProjects';
import { DehydratedProject , isProject, Project } from 'au-js-sdk/lib/models/Project';
import Itly, { ProjectDeletedProperties, ProjectDuplicatedProperties, ProjectEditedProperties } from 'itly';
import { generateLivePreviewUrl } from 'helpers/thumbnails';
import { addItemToCartRequest } from 'store/cart/actions';
import { HammerCategoryType, Product } from 'au-js-sdk/lib/models/Product';
import { HydratedProjectV1 } from 'components/types/projects';
import { getProjectInfo as _getProjectInfo } from './helpers';
import { Resource } from 'au-js-sdk/lib/types/relatives';
import { shouldGoToEditorV2 } from 'helpers/projects/redirect';
import { customerEmailSelector , customerIdSelector } from 'store/customer/selectors';
import { useProductSelectors } from 'hooks/useProductSelectors';
import { useSplitTreatment } from 'hooks/splits/useSplitTreatment';
import { ProjectDataV1 } from 'au-js-sdk/lib/models/ProjectV1';
import { PaginationData } from 'au-js-sdk/lib/api/models/PaginatedResponse';
import { Product as GatsbyProduct } from 'types/Product';
const MAX_PER_PAGE = 10;
const LOADING_THUMBNAIL_URL = 'https://assets.artifactuprising.com/assets/images/misc/shine-loader.gif';
export type CompactProject = Omit<DehydratedProject, 'emptyPageAck' | 'lastModifiedByAdmin' | 'lastModifiedBy' | 'parentId' | 'templateId' | 'templateNameScheme' | 'templateVersion'>;
type UseProjectsResponse = {
  projects: DehydratedProject[];
  isInitialized: boolean;
  isFetching: boolean;
  paginationData: PaginationData;
  fetchProjects: (page: number, pageSize: number) => void;
  fetchProject: (projectId: string, schemaVersion: '1' | '2') => Promise<HydratedProjectV1 | Project | ProjectDataV1 | Resource>;
  deleteProjectHandler: (project: DehydratedProject, editProjectAfter?: boolean) => () => Promise<void>;
  renameProjectHandler: (project: CompactProject) => (newName: string) => Promise<void>;
  duplicateProjectHandler: (project: DehydratedProject, editProjectAfter?: boolean) => Promise<DehydratedProject | Project | HydratedProjectV1 | Resource | ProjectDataV1>;
  addProjectHandler: (project: DehydratedProject, product: GatsbyProduct) => () => void;
  editProjectHandler: (project: CompactProject, editorUrl?: string | null) => () => Promise<void>;
  isEditable: (project: { state?: string; status?: string }) => boolean;
  getProjectThumbnailUrl: (sku: string, options: { [key: string]: string }, schemaVersion: number) => Promise<string | undefined>;
  getEditorUrl: (project: CompactProject, customerEmail: string, customerId: string) => Promise<string>;
  mapProjectToSmallCompactVersion: (project: Project | HydratedProjectV1) => Omit<DehydratedProject, 'emptyPageAck' | 'lastModifiedByAdmin' | 'lastModifiedBy' | 'parentId' | 'templateId' | 'templateNameScheme' | 'templateVersion'>;
  getProjectInfo: (project: DehydratedProject) => string;
  getHasPrints: (project: CompactProject) => boolean;
};
const useProjects = (flashId: string, token: string, pageSize = MAX_PER_PAGE, fetchOnMount?: boolean): UseProjectsResponse => {
  const dispatch = useDispatch();
  const [isFetching, setIsFetching] = useState(false);
  const [isInitialized, setIsInitialized] = useState(true);

  const customerEmail = useSelector(customerEmailSelector);
  const customerId = useSelector(customerIdSelector);

  const {
    getLivePreviewAttributes,
    getBasePDPImage,
    getReportingProductCategory,
    getAttributes,
    getEditorVersion
  } = useProductSelectors();

  const SPLIT_IO_FEATURE_FLAG_TRANSLATION_MASTER = 'Translation_Master';
  const SPLIT_IO_FEATURE_FLAG_TRANSLATION_MASTER_COOKIE = 'au_split_feature_flag_translation_master';
  const { treatmentStatus: isTranslationEnabled } = useSplitTreatment(
    SPLIT_IO_FEATURE_FLAG_TRANSLATION_MASTER,
    SPLIT_IO_FEATURE_FLAG_TRANSLATION_MASTER_COOKIE
  );

  const {
    projects,
    isFetching: isFetching_,
    paginationData,
    fetchProject,
    fetchProjects,
    deleteProject,
    renameProject,
    duplicateProject,
    duplicateAndFetchProjects
  } = _useProjects({
    flashId,
    token,
    fetchOnMount: false,
    url: process.env.GATSBY_PROJECT_SERVICE_URL,
    pageSize,
    schemaVersion: 'all'
  });

  const deleteProjectHandler = (project: DehydratedProject, editProjectAfter = false) => async () => {
    await deleteProject(project.id, project.schemaVersion === 2 ? '2' : '1', editProjectAfter);
    Itly.projectDeleted(createProjectToAnalyticsData(project));
  };

  const renameProjectHandler = (project: DehydratedProject) => async (newName: string) => {
    await renameProject(project.id, newName, project.schemaVersion === 2 ? '2' : '1');
  };

  const duplicateProjectHandler = async (
    project: DehydratedProject,
    editProjectAfter = false
  ): Promise<DehydratedProject | Project | HydratedProjectV1 | Resource | ProjectDataV1> => {
    let duplicateProjectFromHandler: Project | HydratedProjectV1 | Resource | ProjectDataV1;
    if (editProjectAfter) {
      duplicateProjectFromHandler = await duplicateProject(project.id, project.schemaVersion === 2 ? '2' : '1');
    } else {
      duplicateProjectFromHandler = await duplicateAndFetchProjects(
        project.id,
        project.schemaVersion === 2 ? '2' : '1'
      );
    }
    Itly.projectDuplicated(createProjectToAnalyticsData(project));
    return duplicateProjectFromHandler;
  };

  /* Add the page_count extension attribute if the project is a book and has a pageCount attribute set.
    This is needed by the cart controller to get the right price including any additional pages. */
  const productNeedsPageCount = (product?: Product) => {
    return (
      ['books', 'spread books', 'signature spread books'].indexOf(getReportingProductCategory(product?.sku)) !== -1
    );
  };

  const getPageCount = (project: DehydratedProject): number | undefined => {
    const pageCount = productNeedsPageCount(project.product) ? project.product?.pageCount : undefined;
    return pageCount || Number(project.product?.options.standard_page_count);
  };

  const addProjectHandler = (project: DehydratedProject, product: GatsbyProduct) => () => {
    try {
      setIsFetching(true);
      dispatch(
        addItemToCartRequest(
          {
            sku: project.product?.sku.replace(/\+/g, ' '),
            qty: project.product?.qty || 1,
            pageCount: getPageCount(project),
            redirectToEditor: '0',
            projectId: project.id,
            projectVersion: (project.schemaVersion || 1).toString(),
            options: project.product?.options
          },
          {
            product_id: product.productId,
            sku: product.sku,
            name: product.name,
            price: product.price,
            location: product.canonicalUrl,
            is_customizable_product: undefined,
            category: product.reportingProductCategory,
            product_line: product.reportingProductLine
          }
        )
      );
    } catch {
      // All errors should be catched on the corresponding saga, but just in case it is needed
      setIsFetching(false);
    }
  };

  const editProjectHandler = (project: DehydratedProject, editorUrl: string | null = null) => async () => {
    if (!editorUrl) {
      editorUrl = await getEditorUrl(project, customerEmail, typeof customerId === 'string' ? customerId : customerId.toString());
    }

    Itly.projectEdited(createProjectToAnalyticsData(project));

    window.location.href = editorUrl;
  };

  const getProjectThumbnailUrl = async (
    sku: string,
    options: { [key: string]: string },
    schemaVersion: number
  ): Promise<string | undefined> => {
    if (!sku?.length || !options || !schemaVersion) {
      return LOADING_THUMBNAIL_URL;
    }

    const defaultProductImage = getBasePDPImage(sku) || LOADING_THUMBNAIL_URL;
    const livePreviewAttrs = (getLivePreviewAttributes(sku) || []).join(',');

    if (livePreviewAttrs) {
      if (schemaVersion === 1 && 'fabric_color' in options && 'leather_color' in options) {
        return defaultProductImage;
      }
      const url = generateLivePreviewUrl(
        process.env.GATSBY_LIVE_PREVIEW_URL,
        sku,
        livePreviewAttrs,
        options,
        defaultProductImage
      );
      return url;
    }

    return defaultProductImage;
  };

  const getProjectInfo = (project: DehydratedProject) => {
    const attributes = getAttributes(project?.product?.sku);
    return _getProjectInfo(project, attributes);
  };

  const getEditorBaseUrlForProject = (project: CompactProject, shouldGoToEditorV2): string => {
    // For some reason we have seen some projects that do not have a productLine, so we can't determine if
    // They are in the split for the given project
    if (!project.product?.productLine) {
      return project.schemaVersion === 2 || shouldGoToEditorV2
        ? process.env.GATSBY_EDITOR_V2_URL
        : process.env.GATSBY_EDITOR_URL;
    }

    // Update condition to know if the sku is supported on editor v2
    // `project.product.productLine` needs to be equivalent to Magento's product data `reporting_product_category` attribute value
    return project.schemaVersion === 2 || shouldGoToEditorV2
      ? process.env.GATSBY_EDITOR_V2_URL
      : process.env.GATSBY_EDITOR_URL;
  };

  const isEditable = (project: { state?: string; status?: string }): boolean => {
    const projectStatus = project?.status || project?.state;
    return !!['IN_PROGRESS', 'IN_CART'].find(state => state === projectStatus);
  };

  const getEditorUrl = async (project: CompactProject, customerEmail: string, customerId: string) => {
    let shouldGoToEditorV2Result = false;
    if (isTranslationEnabled) {
      shouldGoToEditorV2Result = await shouldGoToEditorV2(
        project.product?.sku,
        getReportingProductCategory(project.product?.sku),
        customerEmail,
        customerId,
        getEditorVersion(project.product?.sku)
      );
    }

    const editorUrl = getEditorBaseUrlForProject(project, shouldGoToEditorV2Result);
    if (!editorUrl || !token || !flashId) {
      return '#';
    }

    const url = new URL(editorUrl);
    url.searchParams.set('projectId', project.id);
    url.searchParams.set('flashId', flashId);
    url.searchParams.set('flashToken', token);
    url.searchParams.set('projectEdited', isEditable(project).toString());
    return url.toString();
  };

  const createProjectToAnalyticsData = (
    project: DehydratedProject
  ): ProjectDeletedProperties & ProjectDuplicatedProperties & ProjectEditedProperties => {
    return {
      project_id: project.id,
      is_customizable_product: !!['IN_PROGRESS', 'IN_CART'].find(state => state === project.status),
      sku: project.product.sku,
      name: project.product.name || '',
      category: getReportingProductCategory(project.product.sku),
      price: project.product.price || 0,
      product_id: project.product.id || 0,
      product_line: project.product.productLine || '',
      product_type: project.product.productType || ''
    };
  };
  const mapProjectToSmallCompactVersion = (project: Project | HydratedProjectV1): CompactProject => {
    if (isProject(project)) {
      return {
        ...project,
        printableGroups: undefined
      };
    }

    const status = (project.state as unknown) as
      | 'IN_PROGRESS'
      | 'ADMIN_EDITING'
      | 'FAILED_TRANSLATION'
      | 'PRINTED'
      | 'TRANSLATING';
    const qty = project.product?.qty === undefined ? undefined : Number(project.product?.qty);
    const price = project.product?.reportingProductProperties?.basePrice ?? undefined;

    return {
      ...project,
      version: Number(project.version),
      createdBy: project.user,
      status,
      printableGroups: undefined,
      schemaVersion: 1,
      createdAt: project?.createdAt ? new Date(project.createdAt).toISOString() : '',
      lastModifiedAt: project?.modifiedAt ? new Date(project.modifiedAt).toISOString() : new Date().toDateString(),
      name: project?.name || 'Untitled Project',
      product: project.product?.sku
        ? {
            sku: project.product?.sku,
            name: project.product?.name,
            hammerCategory: (project.product?.category || '') as HammerCategoryType,
            price,
            qty,
            pageCount: project.product?.pageCount,
            options: project.product?.attributes
          }
        : undefined
    };
  };

  const getHasPrints = (project: DehydratedProject) => (project?.product?.options?.has_prints ?? null) === '1';

  useEffect(() => {
    if (!isInitialized) {
      setIsInitialized(true);
    }
  }, [isInitialized, isFetching]);

  useEffect(() => {
    // Update according to the other isFetching commig from `components`
    setIsFetching(isFetching_);
  }, [isFetching_]);

  useEffect(() => {
    if (flashId && token && fetchOnMount) {
      fetchProjects(1, pageSize);
    }
  // TODO: Refactor this dependency
  // eslint-disable-next-line react-hooks/exhaustive-deps    
  }, [flashId, token, fetchOnMount, pageSize]);

  return {
    projects,
    isInitialized,
    isFetching,
    paginationData,
    fetchProjects,
    fetchProject,
    deleteProjectHandler,
    renameProjectHandler,
    duplicateProjectHandler,
    addProjectHandler,
    editProjectHandler,
    isEditable,
    getProjectThumbnailUrl,
    getEditorUrl,
    mapProjectToSmallCompactVersion,
    getProjectInfo,
    getHasPrints
  };
};

export default useProjects;
